import * as React from "react"
import { Calendar, ExternalLink, ImageIcon } from "lucide-react"
import { CarouselWidget, CarouselWidgetContent, CarouselWidgetItem, CarouselWidgetNext, CarouselWidgetPrevious } from "./carousel-widget"
import { CardWidget, CardWidgetContent, CardWidgetDescription, CardWidgetHeader, CardWidgetTitle } from "./card-widget"
import { cn } from "../../lib/utils"

export interface ContentEntry {
  // Core fields
  title: string
  description: string
  date: string
  link: string
  linkText?: string
  
  // Event-specific fields
  speaker?: string
  time?: string
  location?: string
  price?: string
  duration?: string
  category?: string
  organizer?: string
  capacity?: string
  status?: 'available' | 'sold-out' | 'cancelled'
  
  // Optional content
  image?: string
  details?: string
  extras?: string
}

interface CarouselContentWidgetProps {
  entries: ContentEntry[]
  className?: string
  showImages?: boolean
  itemsPerView?: number
}

const CarouselContentWidget = React.forwardRef<
  React.ElementRef<typeof CarouselWidget>,
  CarouselContentWidgetProps
>(({ entries, className, showImages = true, itemsPerView = 3, ...props }, ref) => {
  const opts = {
    align: "start" as const,
    loop: false,
  }

  // Helper function to validate and clean entry data
  const cleanEntry = (entry: ContentEntry) => {
    const hasValidLink = entry.link && entry.link.trim() && entry.link !== '#'
    const defaultLinkText = entry.speaker ? "Register" : "View"
    
    return {
      ...entry,
      hasValidLink,
      defaultLinkText
    }
  }

  return (
    <CarouselWidget
      ref={ref}
      opts={opts}
      className={cn("dsw-w-full dsw-max-w-full", className)}
      {...props}
    >
      <CarouselWidgetContent className="dsw--ml-4">
        {entries.map((entry, index) => {
          const cleanedEntry = cleanEntry(entry)
          return (
          <CarouselWidgetItem key={index} className={cn("dsw-pl-4", `dsw-basis-1/${itemsPerView}`)}>
            <CardWidget className="dsw-h-full dsw-flex dsw-flex-col">
              {showImages && (
                <div className="dsw-aspect-video dsw-bg-muted dsw-flex dsw-items-center dsw-justify-center dsw-rounded-t-lg">
                  {entry.image ? (
                    <img 
                      src={entry.image} 
                      alt={entry.title}
                      className="dsw-w-full dsw-h-full dsw-object-cover dsw-rounded-t-lg"
                    />
                  ) : (
                    <ImageIcon className="dsw-h-12 dsw-w-12 dsw-text-muted-foreground" />
                  )}
                </div>
              )}
              <CardWidgetHeader className="dsw-flex-1">
                <CardWidgetTitle className="dsw-text-sm dsw-font-semibold dsw-leading-tight dsw-mb-2">{cleanedEntry.title}</CardWidgetTitle>
                <p className="dsw-text-xs dsw-font-medium dsw-text-blue-600 dsw-mb-2">
                  {cleanedEntry.speaker || 'Speaker TBA'}
                </p>
                <CardWidgetDescription className="dsw-text-xs dsw-text-gray-600 dsw-leading-relaxed">
                  {cleanedEntry.description}
                </CardWidgetDescription>
              </CardWidgetHeader>
              <CardWidgetContent className="dsw-pt-0 dsw-mt-auto">
                {/* Event Details */}
                <div className="dsw-space-y-2 dsw-mb-3">
                  {/* Date and Time */}
                  <div className="dsw-flex dsw-items-center dsw-gap-1 dsw-text-xs dsw-text-muted-foreground">
                    <Calendar className="dsw-h-3 dsw-w-3 dsw-flex-shrink-0" />
                    <span className="dsw-truncate">
                      {cleanedEntry.date}
                      <span className="dsw-ml-1">• {entry.time || 'Time TBA'}</span>
                    </span>
                  </div>
                  
                  {/* Additional Details */}
                  {cleanedEntry.price && (
                    <p className="dsw-text-xs dsw-font-medium dsw-text-green-600">
                      {cleanedEntry.price}
                    </p>
                  )}
                  {cleanedEntry.status && cleanedEntry.status !== 'available' && (
                    <span className={cn(
                      "dsw-inline-block dsw-px-2 dsw-py-1 dsw-rounded dsw-text-xs dsw-font-medium",
                      cleanedEntry.status === 'sold-out' && "dsw-bg-red-100 dsw-text-red-800",
                      cleanedEntry.status === 'cancelled' && "dsw-bg-gray-100 dsw-text-gray-800"
                    )}>
                      {cleanedEntry.status === 'sold-out' ? 'Sold Out' : 'Cancelled'}
                    </span>
                  )}
                </div>
                
                {/* Action Button */}
                <div className="dsw-flex dsw-justify-end">
                  {cleanedEntry.hasValidLink ? (
                    <a 
                      href={cleanedEntry.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="dsw-inline-flex dsw-items-center dsw-justify-center dsw-whitespace-nowrap dsw-text-xs dsw-font-medium dsw-ring-offset-background dsw-transition-colors dsw-focus-visible:dsw-outline-none dsw-focus-visible:dsw-ring-2 dsw-focus-visible:dsw-ring-ring dsw-focus-visible:dsw-ring-offset-2 dsw-disabled:dsw-pointer-events-none dsw-disabled:dsw-opacity-50 dsw-cursor-pointer dsw-border dsw-border-input dsw-bg-background hover:dsw-bg-accent hover:dsw-text-accent-foreground dsw-h-8 dsw-px-3 dsw-rounded-md"
                    >
                      <ExternalLink className="dsw-h-3 dsw-w-3 dsw-mr-1" />
                      {cleanedEntry.linkText || cleanedEntry.defaultLinkText}
                    </a>
                  ) : (
                    <span className="dsw-text-xs dsw-text-muted-foreground dsw-italic">
                      Info only
                    </span>
                  )}
                </div>
              </CardWidgetContent>
            </CardWidget>
          </CarouselWidgetItem>
          )
        })}
      </CarouselWidgetContent>
      <CarouselWidgetPrevious />
      <CarouselWidgetNext />
    </CarouselWidget>
  )
})
CarouselContentWidget.displayName = "CarouselContentWidget"

export { CarouselContentWidget } 